/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

const OP_BACK_TO_UI = {
  greater_than: 'greater_than',
  greater_than_or_equal: 'greater_than_or_equal',
  greater_equal: 'greater_than_or_equal',
  greater_equals: 'greater_than_or_equal',
  less_than: 'less_than',
  less_than_or_equal: 'less_than_or_equal',
  less_equal: 'less_than_or_equal',
  less_equals: 'less_than_or_equal',
  equals: 'equals',
  not_equals: 'not_equals',
  not_equal: 'not_equals',
  crosses_above: 'crosses_above',
  crosses_below: 'crosses_below',
  cross_above: 'crosses_above',
  cross_below: 'crosses_below',
  gt: 'greater_than',
  gte: 'greater_than_or_equal',
  lt: 'less_than',
  lte: 'less_than_or_equal',
  eq: 'equals',
  ne: 'not_equals',
  GT: 'greater_than',
  GTE: 'greater_than_or_equal',
  LT: 'less_than',
  LTE: 'less_than_or_equal',
  EQ: 'equals',
  NE: 'not_equals',
  GREATER_THAN: 'greater_than',
  GREATER_THAN_OR_EQUAL: 'greater_than_or_equal',
  LESS_THAN: 'less_than',
  LESS_THAN_OR_EQUAL: 'less_than_or_equal',
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  '<': 'less_than',
  '<=': 'less_than_or_equal',
  '>': 'greater_than',
  '>=': 'greater_than_or_equal',
  '==': 'equals',
  '!=': 'not_equals',
  cross_over: 'crosses_above',
  cross_under: 'crosses_below',
  CROSS_ABOVE: 'crosses_above',
  CROSS_BELOW: 'crosses_below',
  CROSS_OVER: 'crosses_above',
  CROSS_UNDER: 'crosses_below',
};

export function toUiOperator(op) {
  if (!op) return op;
  const key = String(op);
  return OP_BACK_TO_UI[key] || OP_BACK_TO_UI[key.toLowerCase()] || op;
}

function buildIndicatorOperand(nodeId, nodesById) {
  const node = nodesById[nodeId];
  if (!node) return { type: 'indicator', name: 'EMA', params: { length: 9 }, input: { type: 'price', source: 'close' } };
  const name = node.name || 'EMA';
  const params = node.params || {};
  const timeframe = node.timeframe;
  const src = node.inputs?.source;
  let input;
  if (src) {
    const srcNode = nodesById[src];
    if (srcNode) {
      if ((srcNode.name || '').toUpperCase() === 'PRICE') {
    input = { type: 'price', source: srcNode.params?.source || 'close', timeframe: srcNode.timeframe };
      } else {
        input = buildIndicatorOperand(src, nodesById);
      }
    }
  } else {
    input = { type: 'price', source: 'close' };
  }
  const out = { type: 'indicator', name, params, input };
  if (timeframe) out.timeframe = timeframe;
  return out;
}

function toUiOperand(opnd, nodesById) {
  if (opnd == null) return 0;
  if (typeof opnd === 'number') return opnd;
  if (typeof opnd === 'object') {
    // Arithmetic expression nodes (op with lhs/rhs where lhs/rhs exist and op is arithmetic)
    if (opnd.op && (opnd.lhs !== undefined && opnd.rhs !== undefined)) {
      const raw = String(opnd.op).toUpperCase();
      if (['ADD','SUB','MUL','DIV','+','-','*','/'].includes(raw)) {
        return {
          op: raw,
          lhs: toUiOperand(opnd.lhs, nodesById),
          rhs: toUiOperand(opnd.rhs, nodesById),
        };
      }
    }
    // Window predicates
    if (opnd.op === 'ANY_TRUE_LAST_N' || opnd.op === 'ALL_TRUE_LAST_N' || opnd.op === 'COUNT_TRUE_LAST_N' || opnd.op === 'OCCURRED_WITHIN_N') {
      const input = toUiOperand(opnd.expr, nodesById);
      const ui = { kind: 'window_pred', op: opnd.op, n: Math.max(1, opnd.n|0) };
      // If the inner resolves to an object, keep it inline; else store as ref string if already stringy
      if (typeof input === 'object') ui.inputRef = input; else ui.inputRef = String(input || '');
      return ui;
    }
    // Comparator expressions (lhs/op/rhs)
    if (opnd.lhs && opnd.op && (opnd.rhs !== undefined)) {
      return {
        lhs: toUiOperand(opnd.lhs, nodesById),
        op: toUiOperator(opnd.op),
        rhs: toUiOperand(opnd.rhs, nodesById),
      };
    }
    // Unwrap LAG to UI 'lag' property when expr is a series source
    if (opnd.op === 'LAG' || opnd.op === 'SHIFT') {
      const n = Math.max(0, opnd.n|0);
      const inner = toUiOperand(opnd.expr, nodesById);
      if (typeof inner === 'object' && (inner.type === 'indicator' || inner.type === 'price')) {
        return { ...inner, lag: n };
      }
      // If we can't map to indicator/price, return expression as-is for advanced cases
      return { op: 'LAG', n, expr: inner };
    }
    if (opnd.type === 'number') return Number(opnd.value || 0);
    if (opnd.type === 'indicator') {
      // Case 1: reference by nodeId (flat sections path)
      if (opnd.nodeId) {
        const base = buildIndicatorOperand(opnd.nodeId, nodesById);
        // Preserve explicit output when provided (e.g., DMI adx/plus_di/minus_di)
        return opnd.output ? { ...base, output: opnd.output } : base;
      }
      // Case 2: inline indicator spec (grouped path). Build directly from fields
      const mapInput = (inp) => {
        if (!inp) return { type: 'price', source: 'close' };
        if (inp.type === 'price') return { type: 'price', source: inp.source || 'close', timeframe: inp.timeframe };
        if (inp.type === 'indicator') {
          if (inp.nodeId) {
            const built = buildIndicatorOperand(inp.nodeId, nodesById);
            return inp.output ? { ...built, output: inp.output } : built;
          }
          // Nested inline indicator input
          const nested = { type: 'indicator', name: inp.name || 'EMA', params: inp.params || {} };
          const nextIn = mapInput(inp.input);
          if (nextIn) nested.input = nextIn;
          if (inp.output) nested.output = inp.output;
          if (inp.timeframe) nested.timeframe = inp.timeframe;
          return nested;
        }
        return { type: 'price', source: 'close' };
      };
      const result = {
        type: 'indicator',
        name: opnd.name || 'EMA',
        params: opnd.params || {},
      };
      // Our serializer emits nested inputs under `inputs: [...]`.
      // Convert back into UI's singular input shape for editing.
      let input;
      if (Array.isArray(opnd.inputs) && opnd.inputs.length > 0) {
        const first = opnd.inputs[0];
        const refName = (first?.ref || '').toUpperCase();
        if (refName === 'PRICE') {
          input = { type: 'price', source: first?.params?.field || 'close', timeframe: first?.timeframe };
        } else if (typeof first === 'object') {
          // Nested inline indicator operand; recursively map to UI operand
          const nested = toUiOperand(first, nodesById);
          // toUiOperand may return a plain number/string; prefer indicator/price objects
          if (nested && typeof nested === 'object') input = nested;
        }
      }
      if (!input) input = mapInput(opnd.input);
      if (input) result.input = input;
      if (opnd.output) result.output = opnd.output;
      if (opnd.timeframe) result.timeframe = opnd.timeframe;
      return result;
    }
    if (opnd.type === 'price') {
      return { type: 'indicator', name: 'PRICE', params: { source: opnd.source || 'close' }, timeframe: opnd.timeframe };
    }
  }
  return 0;
}

function rulesToClauses(rules, nodesById) {
  return (rules || []).map((r) => ({
    type: 'clause',
    lhs: toUiOperand(r.left, nodesById),
    op: toUiOperator(r.operator),
    rhs: toUiOperand(r.right ?? r.min ?? 0, nodesById),
  }));
}

// Rebuild nested group tree from grouped representation ({op, clauses:[...]} where leaves have lhs/op/rhs)
function expandGroupedTree(node) {
  if (!node) return { op: 'AND', clauses: [] };
  const op = node.op || 'AND';
  const outClauses = [];
  for (const c of node.clauses || []) {
    if (!c) continue;
    // group detection: presence of clauses array without lhs
    if (c.clauses && !c.lhs) {
      outClauses.push(expandGroupedTree(c));
    } else {
      outClauses.push({ type: 'clause', lhs: c.lhs, op: toUiOperator(c.op), rhs: c.rhs });
    }
  }
  return { op, clauses: outClauses };
}

export function graphToUiSections(sectionedSpec) {
  const nodes = sectionedSpec?.nodes || [];
  const sections = sectionedSpec?.sections || [];
  const nodesById = Object.fromEntries(nodes.map((n) => [n.id, n]));

  // If new grouped keys exist, prefer them (they already preserve nesting)
  const hasGrouped = sectionedSpec?.entry_long || sectionedSpec?.entry_short || sectionedSpec?.exit_long || sectionedSpec?.exit_short;
  if (hasGrouped) {
    const convertLeaves = (tree, fallbackOp) => {
      // Convert any leaf with indicator/price operands into UI leaf using nodes map
      if (!tree) return { op: fallbackOp, clauses: [] };
      const walk = (n) => {
        if (!n) return null;
        if (n.clauses && !n.lhs) {
          return { op: n.op || 'AND', clauses: (n.clauses || []).map(walk).filter(Boolean) };
        }
        // leaf
        return {
          type: 'clause',
          lhs: toUiOperand(n.lhs, nodesById),
          op: toUiOperator(n.op),
            rhs: toUiOperand(n.rhs, nodesById),
        };
      };
      const root = walk(tree);
      return root || { op: fallbackOp, clauses: [] };
    };
    return {
      entry_long: expandGroupedTree(convertLeaves(sectionedSpec.entry_long, 'AND')),
      entry_short: expandGroupedTree(convertLeaves(sectionedSpec.entry_short, 'AND')),
      exit_long: expandGroupedTree(convertLeaves(sectionedSpec.exit_long, 'OR')),
      exit_short: expandGroupedTree(convertLeaves(sectionedSpec.exit_short, 'OR')),
    };
  }

  // Legacy path: build from flat sections rules (no grouping retained)
  const out = {
    entry_long: { op: 'AND', clauses: [] },
    entry_short: { op: 'AND', clauses: [] },
    exit_long: { op: 'OR', clauses: [] },
    exit_short: { op: 'OR', clauses: [] },
  };
  for (const s of sections) {
    const name = (s?.name || '').toLowerCase();
    const clauses = rulesToClauses(s?.rules || [], nodesById);
    if (name === 'entry' || name === 'entry_long') out.entry_long = { op: 'AND', clauses };
    else if (name === 'entry_short') out.entry_short = { op: 'AND', clauses };
    else if (name === 'exit' || name === 'exit_long') out.exit_long = { op: 'OR', clauses };
    else if (name === 'exit_short') out.exit_short = { op: 'OR', clauses };
  }
  return out;
}
